import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import type { LineaOrden, OrdenDetalle, OrigenRecepcion } from '@manejo-alcanza/contracts';
import { conUnidad, fecha, pesos, quienSoy, registrarRecepcion } from '../api';
import { Aviso, Boton, Card, Error_, Tabla } from './basicos';

/**
 * F5 en la pantalla: cuánto recibió el cliente de verdad.
 *
 * **No hay ningún selector de estado aquí, y no lo va a haber.** `ENTREGADA` y
 * `ENTREGADA_PARCIAL` salen de comparar lo recibido contra lo pedido; si fueran
 * una opción de un desplegable, alguien elegiría «entregada» un día con prisa y
 * el sistema perdería justo el dato que existe para vigilar.
 *
 * El botón «Se recibió el 100 %» no es un modo aparte: llena cada línea con su
 * cantidad pedida y manda exactamente lo mismo que cualquier otra recepción. Es
 * el caso normal resuelto en un clic, no un camino distinto.
 */
export function PanelRecepcion({ orden }: { orden: OrdenDetalle }): ReactNode {
  const [abierto, setAbierto] = useState(false);

  if (orden.estado === 'ANULADA') return null;

  const faltante =
    orden.valorRecibido === null
      ? null
      : (Number(orden.valorPedido) - Number(orden.valorRecibido)).toFixed(2);

  return (
    <Card titulo="Lo que recibió el cliente">
      {orden.recepcion === null ? (
        <p className="text-sm text-tenue">
          Todavía nadie ha registrado lo que el cliente aceptó en su bodega. Mientras esté
          vacío, esta orden no cuenta como ingreso.
        </p>
      ) : (
        <div className="space-y-3">
          <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
            <Dato etiqueta="Recibido el">{fecha(orden.recepcion.fechaRecepcion)}</Dato>
            <Dato etiqueta="De dónde salió el dato">{NOMBRE_ORIGEN[orden.recepcion.origen]}</Dato>
            {orden.recepcion.documentoSoporte && (
              <Dato etiqueta="Soporte">
                <span className="font-mono text-xs">{orden.recepcion.documentoSoporte}</span>
              </Dato>
            )}
            <Dato etiqueta="Lo registró">
              {orden.recepcion.registradaPor ?? '—'}
              {orden.recepcion.correcciones > 0 && (
                <span className="text-tenue">
                  {' '}
                  · corregido {orden.recepcion.correcciones}{' '}
                  {orden.recepcion.correcciones === 1 ? 'vez' : 'veces'}
                </span>
              )}
            </Dato>
          </dl>

          {orden.recepcion.observaciones && (
            <p className="whitespace-pre-wrap border-t border-borde pt-3 text-sm">
              {orden.recepcion.observaciones}
            </p>
          )}

          {/*
            El faltante en rojo y con su nombre. Es el número que el proyecto
            existe para hacer visible el mismo día, y no tres meses después
            cuando llega el reporte del cliente.
          */}
          {faltante !== null && Number(faltante) > 0 && (
            <div className="rounded-md border border-alerta/30 bg-alerta-tenue px-3 py-2 text-sm text-alerta">
              <strong>Faltaron {pesos(faltante)}</strong> de los {pesos(orden.valorPedido)}{' '}
              pedidos. Cuando exista el registro de alistamiento se sabrá por qué; hoy lo que
              se sabe es que faltaron.
            </div>
          )}
        </div>
      )}

      {!abierto && (
        <div className="mt-3">
          <Boton onClick={() => setAbierto(true)}>
            {orden.recepcion === null ? 'Registrar lo recibido' : 'Corregir'}
          </Boton>
        </div>
      )}

      {abierto && <Formulario orden={orden} alCerrar={() => setAbierto(false)} />}
    </Card>
  );
}

// ---------------------------------------------------------------------------

function Formulario({
  orden,
  alCerrar,
}: {
  orden: OrdenDetalle;
  alCerrar: () => void;
}): ReactNode {
  const cliente = useQueryClient();
  const corrige = orden.recepcion !== null;

  const [fechaRecepcion, setFechaRecepcion] = useState(
    orden.recepcion?.fechaRecepcion ?? hoyEnBogota(),
  );
  const [documentoSoporte, setDocumentoSoporte] = useState(
    orden.recepcion?.documentoSoporte ?? '',
  );
  const [observaciones, setObservaciones] = useState(orden.recepcion?.observaciones ?? '');

  /*
   * El origen se propone segun quien esta digitando, y se puede cambiar.
   *
   * Si entra el repartidor, lo normal es que sea el quien lo vio; si entra
   * comercial, lo normal es que lo este copiando del portal. Proponer no es
   * decidir: quien sabe de donde salio el numero es la persona, no el sistema,
   * y por eso el desplegable sigue ahi.
   */
  const sesion = useQuery({ queryKey: ['yo'], queryFn: quienSoy });
  const [origen, setOrigen] = useState<OrigenRecepcion | null>(
    orden.recepcion?.origen ?? null,
  );
  const origenElegido: OrigenRecepcion =
    origen ?? (sesion.data?.rol === 'REPARTIDOR' ? 'REPARTIDOR' : 'PORTAL_CLIENTE');
  const [motivoCorreccion, setMotivoCorreccion] = useState('');
  const [cantidades, setCantidades] = useState<Record<string, string>>(() =>
    Object.fromEntries(orden.lineas.map((l) => [l.id, l.cantidadRecibida ?? ''])),
  );

  const guardar = useMutation({
    mutationFn: () =>
      registrarRecepcion(orden.id, {
        fechaRecepcion,
        origen: origenElegido,
        ...(documentoSoporte.trim() ? { documentoSoporte: documentoSoporte.trim() } : {}),
        ...(observaciones.trim() ? { observaciones: observaciones.trim() } : {}),
        ...(corrige ? { motivoCorreccion: motivoCorreccion.trim() } : {}),
        lineas: orden.lineas.map((l) => ({
          lineaId: l.id,
          cantidadRecibida: normalizar(cantidades[l.id] ?? ''),
        })),
      }),
    onSuccess: () => {
      alCerrar();
      void cliente.invalidateQueries({ queryKey: ['orden', orden.id] });
      void cliente.invalidateQueries({ queryKey: ['ordenes'] });
    },
  });

  const problemas = orden.lineas
    .map((l) => problemaDeLinea(l, cantidades[l.id] ?? ''))
    .filter((p): p is string => p !== null);

  const recibido = orden.lineas.reduce((suma, l) => {
    const v = normalizar(cantidades[l.id] ?? '');
    return esNumero(v) ? suma + Number(v) * Number(l.precioUnitario) : suma;
  }, 0);

  const faltaMotivo = corrige && motivoCorreccion.trim().length < 4;
  const puedeGuardar = problemas.length === 0 && !faltaMotivo && !guardar.isPending;

  return (
    <div className="mt-4 space-y-4 border-t border-borde pt-4">
      {orden.incidenciasAbiertas > 0 && (
        <Aviso>
          Esta orden tiene {orden.incidenciasAbiertas} cosas por revisar y aun así se puede
          registrar lo recibido: la entrega ocurrió de verdad y ocultarla no arregla lo que
          falta. Lo pendiente sigue arriba, abierto.
        </Aviso>
      )}

      <div className="flex flex-wrap items-end gap-4">
        <Campo etiqueta="Día en que el cliente recibió">
          <input
            type="date"
            value={fechaRecepcion}
            max={hoyEnBogota()}
            min={orden.fechaDocumento}
            onChange={(e) => setFechaRecepcion(e.target.value)}
            className="rounded-md border border-borde bg-superficie px-2 py-1.5 text-sm"
          />
        </Campo>
        {/*
          «Opcional» va en la etiqueta y no en el marcador de posición: el
          marcador desaparece en cuanto escribes, y una advertencia que se borra
          al usarla no advierte de nada. Además el texto largo se cortaba dentro
          de la casilla y no se leía entero.
        */}
        <Campo etiqueta="De dónde sale el dato">
          <select
            value={origenElegido}
            onChange={(e) => setOrigen(e.target.value as OrigenRecepcion)}
            className="rounded-md border border-borde bg-superficie px-2 py-1.5 text-sm"
          >
            <option value="REPARTIDOR">{NOMBRE_ORIGEN.REPARTIDOR}</option>
            <option value="PORTAL_CLIENTE">{NOMBRE_ORIGEN.PORTAL_CLIENTE}</option>
          </select>
        </Campo>
        <Campo etiqueta="Soporte — opcional">
          <input
            value={documentoSoporte}
            onChange={(e) => setDocumentoSoporte(e.target.value)}
            placeholder="remisión, radicado del portal…"
            title="Por donde se vuelve al papel cuando haya que reclamar"
            className="w-80 max-w-full rounded-md border border-borde px-2 py-1.5 text-sm"
          />
        </Campo>
        <Boton
          tipo="secundario"
          onClick={() =>
            setCantidades(
              Object.fromEntries(orden.lineas.map((l) => [l.id, l.cantidadPedida])),
            )
          }
        >
          Se recibió el 100 %
        </Boton>
      </div>

      <Tabla>
        <thead>
          <tr className="border-b border-borde text-left text-xs uppercase tracking-wide text-tenue">
            <th className="px-3 py-2 font-medium">Línea</th>
            <th className="px-3 py-2 text-right font-medium">Pedida</th>
            <th className="px-3 py-2 text-right font-medium">Recibida</th>
            <th className="px-3 py-2 text-right font-medium">Faltó</th>
          </tr>
        </thead>
        <tbody>
          {orden.lineas.map((l) => {
            const valor = cantidades[l.id] ?? '';
            const problema = problemaDeLinea(l, valor);
            const falto = esNumero(normalizar(valor))
              ? Number(l.cantidadPedida) - Number(normalizar(valor))
              : null;

            return (
              <tr key={l.id} className="border-b border-borde last:border-0">
                <td className="px-3 py-2">
                  <div className="font-medium">
                    {l.nombreProducto ?? l.descripcionCliente ?? `Línea ${l.numeroLinea}`}
                  </div>
                  <div className="text-xs text-tenue">
                    línea {l.numeroLinea} · {pesos(l.precioUnitario)} por{' '}
                    {l.unidad === 'KG' ? 'kilo' : 'unidad'}
                  </div>
                </td>
                <td className="cifra px-3 py-2">{conUnidad(l.cantidadPedida, l.unidad)}</td>
                <td className="px-3 py-2 text-right">
                  <input
                    inputMode="decimal"
                    value={valor}
                    onChange={(e) =>
                      setCantidades((c) => ({ ...c, [l.id]: e.target.value }))
                    }
                    aria-label={`Cantidad recibida de la línea ${l.numeroLinea}`}
                    className={`cifra w-28 rounded-md border px-2 py-1.5 text-sm ${
                      problema === null ? 'border-borde' : 'border-alerta'
                    }`}
                  />
                </td>
                <td
                  className={`cifra px-3 py-2 ${falto !== null && falto > 0 ? 'text-alerta' : 'text-tenue'}`}
                >
                  {falto === null ? '—' : falto === 0 ? '—' : conUnidad(String(falto), l.unidad)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Tabla>

      <dl className="grid gap-2 text-sm sm:grid-cols-3">
        <Resumen etiqueta="Pedido" valor={pesos(orden.valorPedido)} />
        <Resumen etiqueta="Recibido" valor={pesos(recibido.toFixed(2))} />
        <Resumen
          etiqueta="Faltante"
          valor={pesos((Number(orden.valorPedido) - recibido).toFixed(2))}
          alerta={Number(orden.valorPedido) - recibido > 0}
        />
      </dl>

      <Campo etiqueta="Observaciones">
        <textarea
          value={observaciones}
          onChange={(e) => setObservaciones(e.target.value)}
          rows={2}
          placeholder="Qué pasó, si pasó algo. Opcional."
          className="w-full rounded-md border border-borde px-2 py-1.5 text-sm"
        />
      </Campo>

      {corrige && (
        <Campo etiqueta="Por qué se corrige">
          <input
            value={motivoCorreccion}
            onChange={(e) => setMotivoCorreccion(e.target.value)}
            placeholder="Obligatorio: una cifra de plata que cambia sin explicación no se puede defender"
            className="w-full rounded-md border border-borde px-2 py-1.5 text-sm"
          />
        </Campo>
      )}

      {problemas.length > 0 && (
        <ul className="space-y-1 text-xs text-alerta">
          {problemas.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      )}
      {guardar.isError && <Error_>{(guardar.error as Error).message}</Error_>}

      <div className="flex flex-wrap gap-2">
        <Boton onClick={() => guardar.mutate()} deshabilitado={!puedeGuardar}>
          {corrige ? 'Guardar la corrección' : 'Registrar'}
        </Boton>
        <Boton tipo="secundario" onClick={alCerrar}>
          Cancelar
        </Boton>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * El origen, dicho como lo diría una persona.
 *
 * `REPARTIDOR` es la fuente de primera mano y va primero: el que entregó es el
 * que sabe. `PORTAL_CLIENTE` llega después y sirve para contrastar — si los dos
 * no coinciden, hay algo que investigar antes de que llegue la nota débito.
 */
const NOMBRE_ORIGEN: Record<OrigenRecepcion, string> = {
  REPARTIDOR: 'Lo reportó el repartidor que entregó',
  PORTAL_CLIENTE: 'Portal / estado de cuenta del cliente',
};

/**
 * Qué está mal en una línea, dicho para quien la está digitando.
 *
 * La misma regla vive en el servicio y en un CHECK de la base. Aquí no es el
 * control —esconder el botón nunca lo es— sino evitar el viaje de ida y vuelta.
 */
function problemaDeLinea(linea: LineaOrden, crudo: string): string | null {
  const v = normalizar(crudo);
  if (v === '') return `Línea ${linea.numeroLinea}: falta la cantidad recibida.`;
  if (!esNumero(v)) return `Línea ${linea.numeroLinea}: «${crudo}» no es una cantidad.`;
  if (Number(v) < 0) return `Línea ${linea.numeroLinea}: no se puede recibir menos que cero.`;
  if (Number(v) > Number(linea.cantidadPedida)) {
    return (
      `Línea ${linea.numeroLinea}: se pidieron ${linea.cantidadPedida} y no se puede ` +
      `recibir ${v}. Recibir más de lo pedido no es un caso raro, es un dato malo.`
    );
  }
  return null;
}

/** La coma decimal de un teclado colombiano, al punto que espera el contrato. */
function normalizar(valor: string): string {
  return valor.trim().replace(',', '.');
}

function esNumero(valor: string): boolean {
  return /^\d+(\.\d+)?$/.test(valor);
}

/** Colombia es siempre UTC-5: sin horario de verano no hay caso raro que cuidar. */
function hoyEnBogota(): string {
  return new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

function Dato({ etiqueta, children }: { etiqueta: string; children: ReactNode }): ReactNode {
  return (
    <div>
      <dt className="text-xs text-tenue">{etiqueta}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function Campo({ etiqueta, children }: { etiqueta: string; children: ReactNode }): ReactNode {
  return (
    <label className="block">
      <span className="mb-1 block text-xs text-tenue">{etiqueta}</span>
      {children}
    </label>
  );
}

function Resumen({
  etiqueta,
  valor,
  alerta = false,
}: {
  etiqueta: string;
  valor: string;
  alerta?: boolean;
}): ReactNode {
  return (
    <div className="rounded-md border border-borde bg-fondo px-3 py-2">
      <dt className="text-xs text-tenue">{etiqueta}</dt>
      <dd className={`cifra !text-left font-semibold ${alerta ? 'text-alerta' : ''}`}>{valor}</dd>
    </div>
  );
}
